
import React, { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Check, ArrowRight } from "lucide-react";
import { Procurement } from "@/types/database.types";
import { sendTelegramNotification } from "@/services/telegram/send";
import { useToast } from "@/hooks/use-toast";

interface Step {
  key: string;
  title: string;
}

interface ProcurementStatusStepperProps {
  currentStatus: string;
  isUpdating: boolean;
  onAdvance: (nextStatus: string) => void;
  userRole?: string;
  paymentTermType?: string;
  procurement?: Procurement;
}

export const ProcurementStatusStepper: React.FC<ProcurementStatusStepperProps> = ({
  currentStatus,
  isUpdating,
  onAdvance,
  userRole,
  paymentTermType = 'upfront',
  procurement
}) => {
  const { toast } = useToast();

  // Determine stages based on payment term type
  // Quality inspection always comes before warehouse
  const PROCUREMENT_STAGES = useMemo(() => {
    if (paymentTermType === 'upfront') {
      // Upfront: Finance Review → Quality → Warehouse Review → Completed
      return [
        { key: "pending_finance", title: "Finance Review" },
        { key: "pending_quality", title: "Quality Inspection" },
        { key: "pending_warehouse", title: "Warehouse Review" },
        { key: "completed", title: "Completed" }
      ];
    } else if (paymentTermType === 'term') {
      // Term: Quality → Warehouse Review → Finance Review → Completed
      return [
        { key: "pending_quality", title: "Quality Inspection" },
        { key: "pending_warehouse", title: "Warehouse Review" },
        { key: "pending_finance", title: "Finance Review" },
        { key: "completed", title: "Completed" }
      ];
    }
    
    // Default to upfront flow
    return [
      { key: "pending_finance", title: "Finance Review" },
      { key: "pending_quality", title: "Quality Inspection" },
      { key: "pending_warehouse", title: "Warehouse Review" },
      { key: "completed", title: "Completed" }
    ];
  }, [paymentTermType]);

  const currentIdx = PROCUREMENT_STAGES.findIndex(s => s.key === currentStatus);
  const isFinal = currentStatus === "completed";

  const nextStatus =
    currentIdx < PROCUREMENT_STAGES.length - 1
      ? PROCUREMENT_STAGES[currentIdx + 1].key
      : null;

  // Determine if user can advance based on role and current status
  const canAdvance = useMemo(() => {
    if (isFinal || !nextStatus) return false;
    
    // Role-based permissions for status advancement
    switch (currentStatus) {
      case 'pending_finance':
        return userRole === 'finance';
      case 'pending_quality':
        return userRole === 'quality';
      case 'pending_warehouse':
        return userRole === 'warehouse';
      default:
        return false;
    }
  }, [currentStatus, userRole, isFinal, nextStatus]);

  const getAdvanceButtonText = () => {
    if (currentStatus === 'pending_finance') {
      return paymentTermType === 'upfront' ? 'Approve & Send to Warehouse' : 'Approve & Complete';
    }
    
    if (currentStatus === 'pending_warehouse') {
      return paymentTermType === 'upfront' ? 'Confirm & Complete' : 'Confirm & Send to Finance';
    }
    
    if (nextStatus && currentIdx >= 0) {
      return `Advance to: ${PROCUREMENT_STAGES[currentIdx + 1].title}`;
    }
    
    return 'Advance';
  };

  const handleAdvance = async () => {
    if (!nextStatus) return;

    try {
      // Call the original onAdvance function
      await onAdvance(nextStatus);

      // Send notification to appropriate team if advancing to pending status
      if (nextStatus === 'pending_finance' && procurement) {
        console.log('Sending notification to finance team for procurement approval');
        
        const notificationMessage = `🔔 **Procurement Finance Review Required**

**Procurement ID:** ${procurement.id}
**Product:** ${procurement.product_name || 'N/A'}
**Quantity:** ${procurement.total_quantity || 'N/A'}
**Total Amount:** Br${procurement.total_amount || 'N/A'}
**Status:** Pending Finance Review

Please review and approve this procurement request.`;

        try {
          await sendTelegramNotification(
            notificationMessage,
            procurement.id
          );
        } catch (notificationError) {
          console.error('Failed to send notification to finance team:', notificationError);
        }
      }

      if (nextStatus === 'pending_quality' && procurement) {
        console.log('Sending notification to quality team for inspection');
        
        const notificationMessage = `🔍 **Quality Inspection Required**

**Procurement ID:** ${procurement.id}
**Product:** ${procurement.product_name || 'N/A'}
**Quantity:** ${procurement.total_quantity || 'N/A'}
**Total Amount:** Br${procurement.total_amount || 'N/A'}
**Status:** Pending Quality Inspection

Please inspect sample size and damage percentage.`;

        try {
          await sendTelegramNotification(
            notificationMessage,
            procurement.id
          );
        } catch (notificationError) {
          console.error('Failed to send notification to quality team:', notificationError);
        }
      }

      if (nextStatus === 'pending_warehouse' && procurement) {
        console.log('Sending notification to warehouse team for procurement review');
        
        const notificationMessage = `📦 **Procurement Warehouse Review Required**

**Procurement ID:** ${procurement.id}
**Product:** ${procurement.product_name || 'N/A'}
**Quantity:** ${procurement.total_quantity || 'N/A'}
**Total Amount:** Br${procurement.total_amount || 'N/A'}
**Status:** Pending Warehouse Review

Please inspect and confirm receipt of this procurement.`;

        try {
          await sendTelegramNotification(
            notificationMessage,
            procurement.id
          );
        } catch (notificationError) {
          console.error('Failed to send notification to warehouse team:', notificationError);
        }
      }
    } catch (error) {
      console.error('Error advancing procurement status:', error);
      toast({
        title: "Error",
        description: "Failed to advance procurement status.",
        variant: "destructive"
      });
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2 flex-wrap">
        {PROCUREMENT_STAGES.map((stage, idx) => {
          return (
            <React.Fragment key={stage.key}>
              <div className="flex items-center gap-1">
                <span
                  className={`rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold ${
                    idx < currentIdx
                      ? "bg-green-500 text-white"
                      : idx === currentIdx
                      ? "bg-blue-500 text-white"
                      : "bg-gray-200 text-gray-500"
                  }`}
                  title={stage.title}
                >
                  {idx < currentIdx ? <Check className="w-4 h-4" /> : idx + 1}
                </span>
                <span className="text-xs min-w-[80px]">{stage.title}</span>
              </div>
              {idx < PROCUREMENT_STAGES.length - 1 && (
                <ArrowRight className="w-4 h-4 text-gray-300" />
              )}
            </React.Fragment>
          );
        })}
      </div>
      
      {canAdvance && (
        <Button
          onClick={handleAdvance}
          disabled={isUpdating}
          size="sm"
          className="w-fit"
        >
          {getAdvanceButtonText()}
        </Button>
      )}
      {!canAdvance && !isFinal && (
        <div className="text-muted-foreground text-sm">
          {userRole === 'seller' 
            ? 'Waiting for approval from the appropriate team'
            : 'You do not have permission to advance this status'
          }
        </div>
      )}
      {isFinal && (
        <div className="text-green-700 font-semibold">
          Process Completed
        </div>
      )}
    </div>
  );
};
